import { BookOpen } from "lucide-react";
import { Eyebrow, H2, P, PageHero, Section } from "@/components/SharedUI";

const lineage = [
  ["Apical Ancestor", "King Robert Brandy"],
  ["Lineage", "Alice Brandy"],
  ["Lore man", "Arthur Dodd"],
  ["Daughter", "Sylvia Jessica Dodd"],
  ["Granddaughter", "Barbara Helen Fernando"],
  ["Mother", "Angelia Graham"],
  ["Mother", "Jamie Graham"],
  ["Current carrier", "Liana Pitt"],
];

const panels = [
  ["Walking alone", "Bigibila moves through Country."],
  ["The hidden emu", "Food, responsibility and truth are tested."],
  ["Refusal to share", "Relationship, obligation and accountability break down."],
  ["The spears arrive", "Consequence comes through the meat ants."],
  ["The quills remain", "Spears become quills, and the lesson becomes protection."],
];

export default function Story() {
  return (
    <>
      <PageHero
        eyebrow="Where Bigibila Got His Quills"
        title="Honouring the old people, the story, the responsibility and the governance teachings carried forward through Bigibila."
        desc="Bigibila is more than a name. Bigibila carries memory, responsibility, protection and governance. The quills are not decoration. They come from story."
      />

      {/* Ancestral provenance */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>Ancestral provenance</Eyebrow>
              <H2 className="text-3xl md:text-4xl">A line of responsibility, carried through family.</H2>
              <P className="mt-6">
                The Bigibila story is grounded in ancestral lineage, cultural obligation, family testimony, language, Lore and lived systems experience — carried through generations of the matrilineal line.
              </P>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-stone-200">
                <table className="w-full text-sm">
                  <tbody>
                    {lineage.map(([role, name]) => (
                      <tr key={`${role}-${name}`} className="border-b border-stone-200 last:border-b-0">
                        <td className="py-4 px-5 text-[10px] tracking-[0.18em] uppercase font-bold text-stone-500 w-1/2 align-top">{role}</td>
                        <td className="py-4 px-5 text-stone-900 font-display font-bold text-lg">{name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <figure className="border border-stone-200 bg-stone-50 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_bigibila-react/artifacts/i0yumqul_1780003207770.png"
                  alt="Barbara Helen Fernando with her granddaughter Liana Pitt — Yuwaalaraay / Gamilaraay matrilineal line."
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  data-testid="story-nan-portrait"
                />
                <figcaption className="border-t border-stone-200 bg-[#FAFAF9] px-5 py-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#9C4221]">River Country · Lineage</div>
                  <div className="mt-2 font-display font-bold text-stone-900 leading-snug">Barbara Helen Fernando &amp; Liana Pitt</div>
                  <p className="mt-2 text-xs leading-relaxed text-stone-700">
                    I come from river Country—born by the Mehi in Moree and raised along the Barwon and Namoi in Walgett. My grandmother Barbara Helen Fernando raised me, surrounded by grandparents Leslie Pitt, Margaret Walford and James Briggs Graham, my bonus fathers and other mothers. I grew up soaking in the stories, culture and love of my old people and mob. Their strength flows through me. I remember and honour those who came before me.
                  </p>
                </figcaption>
              </figure>
            </div>
            <div className="md:col-span-7">
              <p className="text-stone-800 leading-[1.85] text-lg">
                Arthur Dodd was a senior Lore man. He could eat bigibila and described it as sweet meat. Arthur passed Bigibila to his daughter, Sylvia Jessica Dodd. Sylvia passed it to her daughter, Barbara Helen Fernando. Barbara Helen passed it to Liana Pitt: <span className="font-semibold text-stone-900">that Bigibila would be their meat they cannot eat.</span>
              </p>
              <p className="mt-5 text-stone-800 leading-[1.85] text-lg">
                This is not symbolic. It is responsibility. It is a cultural restriction, an obligation, and a governance relationship carried through family, story and Lore.
              </p>
              <div className="mt-8 border-l-2 border-[#9C4221]/40 pl-5">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#9C4221]">Honouring Arthur Dodd</div>
                <p className="mt-2 text-sm text-stone-700 leading-relaxed">
                  Language Revitalist · Senior Lore Man · Son of Alice Brandy · Grandson of King Robert Brandy of Tatala (formerly Angledool).
                </p>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* The quills came from spears — feature card */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="max-w-4xl border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-10">
            <BookOpen className="h-7 w-7 text-[#9C4221] mb-5" strokeWidth={1.5} />
            <h3 className="font-display text-3xl md:text-4xl tracking-tight font-bold text-stone-900">The quills came from spears.</h3>
            <p className="mt-5 text-stone-800 leading-[1.85] text-lg">
              Long ago, Bigibila was walking alone. Two little weebills had taken an emu. Bigibila heard the crash and asked what had fallen, but the weebills denied it. Bigibila cooked and ate the emu himself and refused to share. The weebills went to the meat ants and told them what had happened. The meat ants came with spears and speared Bigibila again and again. Those spears became Bigibila's quills. From then on, Bigibila was covered with quills, covered with spears.
            </p>
          </div>
        </Section>
      </section>

      {/* Story path — 5 panels */}
      <section className="border-b border-stone-200">
        <Section>
          <Eyebrow>Story path</Eyebrow>
          <H2>From harm to protection.</H2>
          <div className="mt-10 grid gap-px bg-stone-200 border border-stone-200 grid-cols-2 md:grid-cols-5">
            {panels.map(([title, desc], i) => (
              <div key={title} className="bg-[#FAFAF9] p-6" data-testid={`story-panel-${i}`}>
                <div className="h-10 w-10 rounded-full border border-[#9C4221]/40 bg-[#9C4221]/[0.08] text-[#9C4221] font-display font-bold flex items-center justify-center mb-4">
                  {i + 1}
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight text-stone-900">{title}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Honouring */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>Honouring those who came before</Eyebrow>
              <H2 className="text-3xl md:text-4xl">The old people carried story, language, Lore, responsibility and survival forward.</H2>
              <P className="mt-6">
                BIGIBILA SYSTEMS™ honours ancestral responsibilities, family memory, language, Lore and governance teachings carried through Bigibila. This work supports Aboriginal governance as a standard government systems should aspire to meet.
              </P>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
              {[
                ["Quills", "Protection controls"],
                ["Spears", "Consequence and escalation"],
                ["Story", "Governance instruction"],
                ["Old people", "Authority and continuity"],
                ["Country", "Jurisdiction and responsibility"],
                ["Future generations", "Legacy and protection"],
              ].map(([a, b]) => (
                <div key={a} className="bg-[#FAFAF9] p-5">
                  <div className="font-display font-bold text-[#9C4221]">{a}</div>
                  <div className="mt-2 text-xs leading-5 text-stone-600">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-12 text-sm text-stone-500 italic max-w-3xl">
            With respect to all Custodians, Elders and Old People — past, present and emerging.
          </p>
        </Section>
      </section>
    </>
  );
}
